import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { TRACKER_MAX_PLANNED_ITEMS, type TrackerCadence } from '@awwyeah/shared';
import { useUser } from '../auth';
import { Sheet } from './components/Sheet';
import { DayTimeline } from './components/DayTimeline';
import {
  addPlannedItem,
  removePlannedItem,
  useTrackerRegistry,
  useTrackerTaps,
  usePlannedItems,
} from './hooks';
import { useHouseholdRoster } from './useHouseholdRoster';

/**
 * A tracker's day view (2026-09-25 — the dog log wanted its own page: "the planned schedule
 * next to what actually happened"). Generic over any tracker, not just the dog's, because
 * the plan-vs-actual question is the same one for any tracker somebody bothers to schedule
 * — this just happens to be the first one that wants a plan at all.
 *
 * Not in the bottom nav (same call as the module's other surfaces): reached from a tracker
 * card's day-view button, `/trackers/:trackerId`, with today as the default and `?d=` for
 * the day being viewed — same deep-link shape as the calendar's day view.
 */
function parseDayParam(raw: string | null): Date | null {
  if (!raw) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!match) return null;
  const [, year, month, day] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  if (Number.isNaN(parsed.getTime())) return null;
  if (parsed.getMonth() !== Number(month) - 1 || parsed.getDate() !== Number(day)) return null;
  return parsed;
}

function toDayParam(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function dayLabel(day: Date, today: Date): string {
  if (day.toDateString() === today.toDateString()) return 'Today';
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (day.toDateString() === yesterday.toDateString()) return 'Yesterday';
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (day.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  return day.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

export function TrackerDayPage(): JSX.Element {
  const { trackerId } = useParams<{ trackerId: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const { trackers } = useTrackerRegistry();
  const tracker = trackers.find((t) => t.id === trackerId) ?? null;

  const [searchParams, setSearchParams] = useSearchParams();
  const now = useMemo(() => new Date(), []);
  const day =
    parseDayParam(searchParams.get('d')) ??
    new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const { taps } = useTrackerTaps(trackerId ?? null);
  const { map: plannedMap, items: planned } = usePlannedItems(trackerId ?? null);
  const { getMember } = useHouseholdRoster();

  const [addingPlan, setAddingPlan] = useState(false);

  const goToDay = (next: Date): void => {
    const params = new URLSearchParams(searchParams);
    params.set('d', toDayParam(next));
    setSearchParams(params, { replace: true });
  };

  const shiftDay = (deltaDays: number): void => {
    const next = new Date(day);
    next.setDate(next.getDate() + deltaDays);
    goToDay(next);
  };

  if (!user) return <></>;

  if (!tracker) {
    return (
      <div className="flex flex-col gap-3 py-6" data-testid="tracker-day-page-missing">
        <p className="text-sm text-fg-3">That tracker isn&apos;t around any more.</p>
        <button
          type="button"
          onClick={() => navigate('/apps')}
          className="self-start text-sm font-semibold text-fg-2"
        >
          Back to Apps
        </button>
      </div>
    );
  }

  const isToday = day.toDateString() === now.toDateString();

  return (
    <div className="flex flex-col gap-3 py-2" data-testid="tracker-day-page">
      <header className="-mx-4 flex items-center gap-2 border-b border-line bg-surface px-2 py-2 sm:mx-0 sm:rounded-t-md">
        <button
          type="button"
          aria-label="Back"
          data-testid="tracker-day-back"
          onClick={() => navigate(-1)}
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center"
        >
          <ChevronLeft size={20} />
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate font-display text-lg tracking-tight text-fg">
            {tracker.emoji ? `${tracker.emoji} ` : ''}
            {tracker.name} log
          </div>
        </div>
        <button
          type="button"
          data-testid="tracker-day-add-plan"
          aria-label="Add a planned item"
          onClick={() => setAddingPlan(true)}
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center text-fg-2"
        >
          <Plus size={18} />
        </button>
      </header>

      <div className="flex items-center justify-between px-1">
        <button
          type="button"
          aria-label="Previous day"
          data-testid="tracker-day-prev"
          onClick={() => shiftDay(-1)}
          className="flex h-10 w-10 items-center justify-center rounded-full text-fg-2 hover:bg-raised"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="flex flex-col items-center">
          <span className="text-sm font-semibold text-fg" data-testid="tracker-day-label">
            {dayLabel(day, now)}
          </span>
          {!isToday && (
            <button
              type="button"
              data-testid="tracker-day-today"
              onClick={() => goToDay(new Date(now.getFullYear(), now.getMonth(), now.getDate()))}
              className="text-xs font-semibold text-fg-3 underline"
            >
              Back to today
            </button>
          )}
        </div>
        <button
          type="button"
          aria-label="Next day"
          data-testid="tracker-day-next"
          onClick={() => shiftDay(1)}
          className="flex h-10 w-10 items-center justify-center rounded-full text-fg-2 hover:bg-raised"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      <DayTimeline
        actions={tracker.actions}
        planned={planned}
        taps={taps}
        day={day}
        now={now}
        isToday={isToday}
        getMember={getMember}
      />

      <Sheet
        open={addingPlan}
        onClose={() => setAddingPlan(false)}
        title="Add to the plan"
        testId="tracker-plan-sheet"
      >
        <PlanForm
          actions={tracker.actions}
          atCap={planned.length >= TRACKER_MAX_PLANNED_ITEMS}
          onAdd={(draft) => {
            addPlannedItem(plannedMap, draft, user.id);
          }}
          onClose={() => setAddingPlan(false)}
        />
        {planned.length > 0 && (
          <div className="mt-4 flex flex-col gap-2 border-t border-line pt-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-fg-3">
              Current plan
            </span>
            {planned.map((item) => (
              <div
                key={item.id}
                data-testid="tracker-plan-row"
                className="flex items-center gap-2 rounded-sm border border-line px-3 py-2"
              >
                <span className="min-w-0 flex-1 text-sm text-fg">
                  <span className="font-semibold">{item.label}</span> · {item.time}
                </span>
                <button
                  type="button"
                  data-testid="tracker-plan-remove"
                  aria-label={`Remove ${item.label} from the plan`}
                  onClick={() => removePlannedItem(plannedMap, item.id)}
                  className="shrink-0 rounded px-2 py-1 text-xs font-semibold text-danger"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </Sheet>
    </div>
  );
}

function PlanForm({
  actions,
  atCap,
  onAdd,
  onClose,
}: {
  actions: readonly { id: string; label: string; cadence: TrackerCadence }[];
  atCap: boolean;
  onAdd: (draft: { label: string; time: string; kind?: string }) => void;
  onClose: () => void;
}): JSX.Element {
  const [label, setLabel] = useState('');
  const [time, setTime] = useState('08:00');
  const [kind, setKind] = useState('');

  if (atCap) {
    return (
      <p className="text-sm text-fg-3">
        That&apos;s {TRACKER_MAX_PLANNED_ITEMS} planned items — as many as fit on a day.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3 pb-2">
      <div className="flex gap-2">
        <input
          aria-label="What's planned"
          data-testid="plan-label-input"
          className="h-11 flex-1 rounded border-2 border-line-strong bg-bg px-3 text-base text-fg"
          placeholder="Walk"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <input
          type="time"
          aria-label="Planned time"
          data-testid="plan-time-input"
          className="h-11 rounded border-2 border-line-strong bg-bg px-2 text-base text-fg"
          value={time}
          onChange={(e) => setTime(e.target.value)}
        />
      </div>
      {actions.length > 0 && (
        <select
          aria-label="Matches which button"
          data-testid="plan-kind-select"
          className="h-11 rounded border-2 border-line-strong bg-bg px-2 text-sm text-fg"
          value={kind}
          onChange={(e) => setKind(e.target.value)}
        >
          <option value="">Doesn&apos;t match a button</option>
          {actions.map((action) => (
            <option key={action.id} value={action.id}>
              Matches &ldquo;{action.label}&rdquo;
            </option>
          ))}
        </select>
      )}
      <button
        type="button"
        data-testid="plan-add-button"
        disabled={!label.trim() || !time}
        onClick={() => {
          onAdd({ label: label.trim(), time, ...(kind ? { kind } : {}) });
          setLabel('');
          onClose();
        }}
        className="h-11 rounded-md bg-inverse text-sm font-semibold text-inverse-fg disabled:opacity-50"
      >
        Add to plan
      </button>
    </div>
  );
}
